// Home v2 — mobile bottom tab bar. Reusable across screens: mirrors the most
// used sidebar destinations. Fixed to the bottom of the viewport on web.
//
// Layout: [Home, Activity] · (+ FAB) · [Apps, Setting]. The center FAB opens a
// pop-up action menu (Unshield / Shield / Receive / Send) anchored above it.
import React, { useState } from 'react'
import { Pressable, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import { Mode } from '@web/contexts/appModeContext'

import { ModeEnum } from '@web/contexts/appModeContext'
import {
  isPrivateModeBlocked,
  PRIVATE_MODE_TOOLTIP,
  PrivateModeReason
} from '@web/utils/privateModeAvailability'

import { BOTTOM_TABS, FAB_ACTIONS, NavItem as NavItemType, NavKey } from '../navConfig'
import { getModeAccent, LAYOUT, RADII } from '../tokens'
import usePrivateModeReason from '../usePrivateModeReason'
import { PlusIcon } from './HomeIcons'

type Props = {
  active: NavKey
  mode: Mode
  onNavigate: (item: NavItemType) => void
}

const Tab = ({
  item,
  active,
  onNavigate
}: {
  item: NavItemType
  active: boolean
  onNavigate: (item: NavItemType) => void
}) => {
  const { theme } = useTheme()
  const Icon = item.icon
  const color = (active ? theme.primaryText : theme.secondaryText) as string
  return (
    <Pressable
      onPress={() => onNavigate(item)}
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        paddingVertical: 8
      }}
    >
      {/* Active top-indicator bar (Figma Derco/Blue rule) */}
      {active && (
        <View
          style={{
            position: 'absolute',
            top: 0,
            width: 36,
            height: 3,
            borderRadius: 2,
            backgroundColor: theme.primary as string
          }}
        />
      )}
      <Icon width={24} height={24} color={color} />
      <Text fontSize={11} weight={active ? 'semiBold' : 'regular'} color={color}>
        {item.label}
      </Text>
    </Pressable>
  )
}

const BottomTabBar: React.FC<Props> = ({ active, mode, onNavigate }) => {
  const { theme } = useTheme()
  const accent = getModeAccent(theme, mode)
  const [menuOpen, setMenuOpen] = useState(false)
  // Private-key / hardware / view-only accounts can't open a private wallet — the
  // shield/unshield/send FAB actions are disabled (with a tooltip) for them.
  const privateModeReason = usePrivateModeReason()
  const privateBlocked = isPrivateModeBlocked(privateModeReason)
  const blockedTooltip =
    PRIVATE_MODE_TOOLTIP[privateModeReason as Exclude<PrivateModeReason, 'available'>]
  // On iOS home-screen PWA the bar otherwise sits under the home indicator.
  // 0 in a normal browser tab. (env(safe-area-inset-bottom), viewport-fit=cover.)
  const insets = useSafeAreaInsets()

  const left = BOTTOM_TABS.slice(0, 2)
  const right = BOTTOM_TABS.slice(2, 4)
  // Public-only actions (Swap & Bridge) are hidden in private mode.
  const fabActions = FAB_ACTIONS.filter((item) => !item.publicOnly || mode === ModeEnum.PUBLIC)

  const onAction = (item: NavItemType) => {
    setMenuOpen(false)
    onNavigate(item)
  }

  return (
    <>
      {/* Action menu overlay — scrim + right-aligned pill stack above the FAB. */}
      {menuOpen && (
        <>
          <Pressable
            onPress={() => setMenuOpen(false)}
            style={{
              position: 'fixed' as any,
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 50,
              backgroundColor: 'rgba(0, 0, 0, 0.6)'
            }}
          />
          <View
            style={{
              position: 'fixed' as any,
              right: 20,
              bottom: LAYOUT.bottomTabHeight + 16 + insets.bottom,
              zIndex: 51,
              alignItems: 'flex-end',
              gap: 12
            }}
          >
            {fabActions.map((item) => {
              const Icon = item.icon
              // Shield/Unshield are inherently private → always disabled when
              // blocked. Send is public in public mode; only its private-mode
              // variant needs a private wallet, so gate it on private mode.
              const disabled =
                privateBlocked &&
                !!item.privateAction &&
                (item.key !== 'send' || mode === ModeEnum.PRIVATE)
              const pill = (
                <Pressable
                  key={item.key}
                  disabled={disabled}
                  onPress={() => onAction(item)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10,
                    paddingVertical: 12,
                    paddingHorizontal: 22,
                    borderRadius: RADII.pill,
                    backgroundColor: theme.primary as string,
                    opacity: disabled ? 0.5 : 1
                  }}
                >
                  <Icon width={22} height={22} color="#fff" />
                  <Text fontSize={16} weight="semiBold" color="#fff">
                    {item.label}
                  </Text>
                </Pressable>
              )

              // Wrap the disabled pill so the global tooltip can anchor to it (a
              // disabled Pressable still forwards pointermove to the wrapper).
              if (disabled) {
                return (
                  <View
                    key={item.key}
                    // @ts-ignore - web-only data attribute consumed by <GlobalTooltip />
                    dataSet={createGlobalTooltipDataSet({
                      id: `fab-${item.key}-disabled`,
                      content: blockedTooltip
                    })}
                  >
                    {pill}
                  </View>
                )
              }

              return pill
            })}
          </View>
        </>
      )}

      {/* Tab bar */}
      <View
        nativeID="zkx-bottom-tab-bar"
        // react-native-web supports position: 'fixed'; RN ViewStyle types don't.
        style={{
          position: 'fixed' as any,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 52,
          flexDirection: 'row',
          alignItems: 'center',
          // Theme-driven so light + dark both read correctly (no hardcoded hex).
          backgroundColor: theme.primaryBackground as string,
          backdropFilter: 'blur(16px)' as any,
          borderTopWidth: 1,
          borderTopColor: theme.secondaryBorder as string,
          paddingHorizontal: 6,
          // iOS home-screen PWA (standalone) home-indicator inset. Grow the bar by
          // insets.bottom and pad the bottom by the same amount so the tab row keeps
          // its height and the bar's background fills the indicator area. 0 in a
          // normal browser tab. Applied via the JS safe-area probe (same as the top
          // inset in AppShell) — env() in CSS proved unreliable for this bar.
          height: LAYOUT.bottomTabHeight + insets.bottom,
          paddingBottom: insets.bottom
        }}
      >
        {left.map((item) => (
          <Tab key={item.key} item={item} active={item.key === active} onNavigate={onNavigate} />
        ))}

        {/* Center FAB — 56×40 pill (r20), vertically centered (the row is
            alignItems:center), that toggles the action menu. */}
        <View style={{ width: 72, alignItems: 'center', justifyContent: 'center' }}>
          <Pressable
            onPress={() => setMenuOpen((v) => !v)}
            style={{
              width: 56,
              height: 40,
              borderRadius: 20,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: accent
            }}
          >
            {/* Rotate only the glyph (not the pill) so the button keeps its shape. */}
            <View style={{ transform: [{ rotate: menuOpen ? '45deg' : '0deg' }] as any }}>
              <PlusIcon size={24} color="#fff" />
            </View>
          </Pressable>
        </View>

        {right.map((item) => (
          <Tab key={item.key} item={item} active={item.key === active} onNavigate={onNavigate} />
        ))}
      </View>
    </>
  )
}

export default React.memo(BottomTabBar)
